import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { alpha } from '@mui/system/colorManipulator';
import { Box, Card, CardContent, Stack, Typography, TextField, SvgIcon, Button, Avatar }from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

// Source
import { useTranslation } from 'react-i18next';
import { appStr } from 'src/locales/app_strings';
import { FileUploader } from 'src/components/uploads/file-uploader';
import { useDialog } from 'src/hooks/use-dialog';
import { fileToBase64, base64ToBlob } from 'src/utils/file-to-base64';
import { postFormData, postData } from 'src/services/RestApi';
import { getInfo, saveInfo } from 'src/services/CacheStorage';
import { useEmailController } from './emailController'; // Import the controller
import useToast from 'src/utils/toast-utils';

const { toastSuccess, toastError } = useToast();

const apiChangeAvatar = async (cover) => {
  const contentType = 'image/png';
  const blob = base64ToBlob(cover, contentType);

  // Now you can use the Blob to create a File object or for other purposes
  // const file = new File([blob], 'image.png', { type: contentType });

  // Create FormData
  const formData = new FormData();
  formData.append('image', blob, 'avatar.png');

  const { result, error } = await postFormData('api/account/change/avatar/', formData);
  if(error) return toastError({message: 'Đổi hình đại diện thất bại'});;

  // Get and refresh avatar
  const user = getInfo();
  user.avatar = result;
  await saveInfo(user);

  toastSuccess('Đổi hình đại diện thành công', {action: 'reload'});
};

const apiChangeEmail = async (email, t) => {
  const queryParams = {
    email: email.toLowerCase(),
  };

  const { result, error } = await postData(
    'api/account/change/email/',
    queryParams,
  );
  if(error) return toastError({message: 'Đổi email thất bại'});
  
  // Get and refresh avatar
  const user = getInfo();
  user.email = result;
  await saveInfo(user);

  toastSuccess('Đổi email thành công');
};

export const AccountGeneralSettings = (props) => {
  const { t } = useTranslation();
  const { avatar, email, name } = props;
  const uploadDialog = useDialog();

  // Use the controller for managing email logic
  const { isEditing, formEmail, isError, newEmailChange, handleEdit } =
    useEmailController(email, t, apiChangeEmail);

  const [cover, setCover] = useState();
  const handleFileSelected = useCallback(async ([file]) => {
    const data = await fileToBase64(file);
    setCover(data);
  }, []);

  const changeAvatar = async () => {
    if (!cover) {
      toastError({message: 'Vui lòng chọn hình đại diện trước'});
      return;
    }
    await apiChangeAvatar(cover);
  };

  return (
    <Card>
      <CardContent>
        <Grid container spacing={3}>
          <Grid xs={12} md={3}>
            <Typography variant="h6"></Typography>
          </Grid>
          <Grid xs={12} md={9}>
            <Stack spacing={3}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <Box
                  sx={{
                    borderColor: 'neutral.300',
                    borderRadius: '50%',
                    borderStyle: 'dashed',
                    borderWidth: 1,
                    p: '4px',
                  }}
                  onClick={uploadDialog.handleOpen}
                >
                  <Box
                    sx={{
                      borderRadius: '50%',
                      height: '100%',
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        backgroundColor: (theme) =>
                          alpha(theme.palette.neutral[700], 0.5),
                        borderRadius: '50%',
                        color: 'common.white',
                        cursor: 'pointer',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        left: 0,
                        opacity: 0,
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        zIndex: 1,
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <SvgIcon color="inherit">
                          <PhotoCameraOutlinedIcon />
                        </SvgIcon>
                        <Typography
                          color="inherit"
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {t(appStr.txt_047)}
                        </Typography>
                      </Stack>
                    </Box>
                    {cover ? (
                      <Avatar
                        src={cover}
                        sx={{
                          height: 100,
                          width: 100,
                        }}
                      >
                        <SvgIcon>
                          <PersonOutlineIcon />
                        </SvgIcon>
                      </Avatar>
                    ) : (
                      <Avatar
                        src={avatar}
                        sx={{
                          height: 100,
                          width: 100,
                        }}
                      >
                        <SvgIcon>
                          <PersonOutlineIcon />
                        </SvgIcon>
                      </Avatar>
                    )}
                  </Box>
                </Box>
                <Button color="inherit" size="small" onClick={changeAvatar}>
                  {t(appStr.txt_048)}
                </Button>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={1}>
                <TextField
                  defaultValue={name}
                  label={t(appStr.txt_049)}
                  disabled
                  required
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderStyle: 'dashed',
                    },
                  }}
                  variant='outlined'
                />
                <Button color="inherit" size="small" disabled>
                  {t(appStr.txt_050)}
                </Button>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={1}>
                <TextField
                  disabled={!isEditing}
                  value={formEmail}
                  onChange={newEmailChange}
                  error={!!isError}
                  // helperText={emailError}
                  label="Email"
                  sx={{ flexGrow: 1 }}
                  variant='outlined'
                />
                <Button onClick={handleEdit} disabled={isError}>
                  {isEditing ? t(appStr.txt_051) : t(appStr.txt_050)}
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
      <FileUploader
        onClose={uploadDialog.handleClose}
        open={uploadDialog.open}
        onFileSelected={handleFileSelected}
        //onUploadClicked={handleUploadClicked}
      />
    </Card>
  );
};

AccountGeneralSettings.propTypes = {
  avatar: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};
